import React, { useRef } from "react";
import { View, FlatList, Platform, TouchableOpacity } from "react-native";

import { Text, Button, Separator } from "components";
import tinycolor from "tinycolor2";

import { sizes, colors } from "assets/constants";
import { factorItemsAtom, factorStepsAtom } from "stores";
import { useRecoilValue } from "recoil";
import { useChmod } from "hooks";
import css from "styles";

const viewOffset = sizes.screenW / 2 - 30;
const ListSeparator = () => <Separator size={2} axis="x" />;

interface FactorStepsProps {
  activeStepPriority: number;
  onChangeStep: (activeStepPriority: number) => void;
  nonEmptySteps: { [key: string]: number };
}

const FactorSteps: React.FC<FactorStepsProps> = ({
  activeStepPriority = 1,
  onChangeStep,
}) => {
  const steps = useRecoilValue(factorStepsAtom);
  const { factorStepsAllowed } = useChmod();
  const factorItems = useRecoilValue(factorItemsAtom);

  const allowedSteps = steps?.filter((e) =>
    factorStepsAllowed().some((k) => k === e.id)
  );

  const stepsListRef = useRef<FlatList>(null);

  const _renderStep = (params: { item: FactorStatusT; index: number }) => {
    const { item, index } = params;

    const currentIndex =
      Platform.OS === "web" ? allowedSteps?.length - index - 1 : index;

    const active = activeStepPriority === Number(item?.priority);

    // const stepCount = Object.keys(nonEmptySteps).find((s) => s === item.id)
    //   ? item.id && nonEmptySteps[item.id]
    //   : 0;

    const stepCount = factorItems?.filter(
      (e) => e.status.priority === item.priority
    ).length;

    const _onChangeStep = () => {
      stepsListRef.current?.scrollToIndex({
        index: currentIndex,
        viewOffset,
        animated: true,
      });

      item?.priority && onChangeStep(item?.priority);
    };

    return (
      <Button
        style={{
          width: 80,
          height: 47,
          borderWidth: 1,
          borderRadius: 15,
          backgroundColor: active
            ? tinycolor(colors.theme).lighten(50).toString()
            : colors.lightBorder,
          borderColor: active
            ? tinycolor(colors.theme).lighten(40).toString()
            : colors.lightBorder,
          // borderColor: active ? colors.theme + "88" : colors.placeholder + "33",
        }}
        bgColor={colors.white}
        renderContent={() => {
          return (
            <TouchableOpacity
              onPress={_onChangeStep}
              activeOpacity={0.9}
              style={{
                paddingTop: 5,
                ...css.container,
                ...css.colCenterAlignX,
                ...css.justifySpaceEvenly,
                backgroundColor: active ? colors.theme + "05" : colors.white,
              }}
            >
              <Text
                style={{ lineHeight: 15 }}
                color={active ? colors.theme : colors.textColorDefault}
                weight="extraBold"
                align="center"
                size={"xxs"}
              >
                {item.title}
              </Text>

              {
                <View style={css.centralElements}>
                  <Text
                    weight="extraBold"
                    style={{ bottom: 0 }}
                    color={active ? colors.theme : colors.textColorDefault}
                  >
                    {stepCount}
                  </Text>
                </View>
              }
            </TouchableOpacity>
          );
        }}
      />
    );
  };

  return (
    <View>
      <FlatList
        onLayout={() => {
          const itemIndex = allowedSteps?.findIndex(
            (e) => e.priority === activeStepPriority
          );

          const currentIndex =
            Platform.OS === "web"
              ? allowedSteps?.length - itemIndex - 1
              : itemIndex;

          if (itemIndex === 0) {
            return;
          }

          stepsListRef.current?.scrollToIndex({
            index: currentIndex,
            viewOffset,
            animated: true,
          });
        }}
        ref={stepsListRef}
        inverted
        horizontal
        contentContainerStyle={{
          paddingTop: sizes.space / 1.5,
          paddingBottom: sizes.space / 3,
          paddingHorizontal: sizes.space,
          ...css.growContainer,
          ...css.centralElements,
        }}
        data={allowedSteps}
        renderItem={_renderStep}
        keyExtractor={(_, key) => String(key)}
        ItemSeparatorComponent={ListSeparator}
        showsHorizontalScrollIndicator={false}
      />
    </View>
  );
};

export default FactorSteps;
